import SortOrder from '~/client/src/shared/enums/SortOrder'
import { compareGroupsByLabel } from '~/client/src/shared/utils/compareGroupsByLabel'

import { EMPTY_OPTION, isEmptyValue } from './emptiness'
import type { FacetIndex } from './facets'
import { resolveOptionLabel } from './facets'
import type { ResolvedBandLevel } from './schema'

const arrayBandWarnings = new Set<string>()

export interface BandTreeRecord<T> {
  readonly kind: 'record'
  readonly record: T
  readonly recordId: string
}

export interface BandTreeNode<T> {
  readonly kind: 'band'
  readonly id: string
  readonly level: number
  readonly field: string
  readonly optionId: string
  readonly label: string
  readonly count: number
  readonly selectableIds: readonly string[]
  readonly children: ReadonlyArray<BandTreeNode<T> | BandTreeRecord<T>>
}

export function encodeBandSegment(field: string, optionId: string): string {
  return `band:${encodeURIComponent(field)}:${encodeURIComponent(optionId)}`
}

export function buildBandId(
  path: ReadonlyArray<{ readonly field: string; readonly optionId: string }>,
): string {
  return path
    .map(segment => encodeBandSegment(segment.field, segment.optionId))
    .join('/')
}

export function buildBandTree<T>(
  records: readonly T[],
  bandLevels: readonly ResolvedBandLevel<T>[],
  getId: (record: T) => string,
  labelsByField: FacetIndex['labelsByField'],
  emptyOptionLabel: string | undefined,
  bandSortKey: string | null = null,
  bandSortOrder: SortOrder = SortOrder.DEFAULT,
): ReadonlyArray<BandTreeNode<T> | BandTreeRecord<T>> {
  if (bandLevels.length === 0) {
    return records.map(record => ({
      kind: 'record',
      record,
      recordId: getId(record),
    }))
  }

  return buildLevel(
    records,
    bandLevels,
    getId,
    labelsByField,
    emptyOptionLabel,
    bandSortKey,
    bandSortOrder,
    0,
    [],
  )
}

function buildLevel<T>(
  records: readonly T[],
  bandLevels: readonly ResolvedBandLevel<T>[],
  getId: (record: T) => string,
  labelsByField: FacetIndex['labelsByField'],
  emptyOptionLabel: string | undefined,
  bandSortKey: string | null,
  bandSortOrder: SortOrder,
  level: number,
  path: ReadonlyArray<{ readonly field: string; readonly optionId: string }>,
): readonly BandTreeNode<T>[] {
  const bandLevel = bandLevels[level]
  if (!bandLevel) return []
  const levelEmptyOptionLabel =
    bandLevel.field.emptyOptionLabel ?? emptyOptionLabel

  const groups = new Map<string, T[]>()
  for (const record of records) {
    const optionId = toBandOptionId(
      bandLevel.field.value(record),
      bandLevel.fieldKey,
    )
    const group = groups.get(optionId)
    if (group) {
      group.push(record)
    } else {
      groups.set(optionId, [record])
    }
  }

  const orderedOptionIds = orderOptionIds(
    [...groups.keys()],
    records,
    bandLevel,
    labelsByField,
    levelEmptyOptionLabel,
    bandSortKey === bandLevel.fieldKey ? bandSortOrder : SortOrder.DEFAULT,
  )

  return orderedOptionIds.map(optionId => {
    const groupRecords = groups.get(optionId) ?? []
    const nextPath = [...path, { field: bandLevel.fieldKey, optionId }]
    const children =
      level + 1 < bandLevels.length
        ? [
            ...buildLevel(
              groupRecords,
              bandLevels,
              getId,
              labelsByField,
              emptyOptionLabel,
              bandSortKey,
              bandSortOrder,
              level + 1,
              nextPath,
            ),
          ]
        : groupRecords.map(record => ({
            kind: 'record' as const,
            record,
            recordId: getId(record),
          }))
    const selectableIds = collectSelectableIds(children)

    return {
      kind: 'band',
      id: buildBandId(nextPath),
      level,
      field: bandLevel.fieldKey,
      optionId,
      label: resolveOptionLabel(
        labelsByField[bandLevel.fieldKey],
        optionId,
        levelEmptyOptionLabel,
      ),
      count: selectableIds.length,
      selectableIds,
      children,
    }
  })
}

function toBandOptionId(value: unknown, fieldKey: string): string {
  if (isEmptyValue(value)) return EMPTY_OPTION
  if (Array.isArray(value)) {
    if (
      process.env.NODE_ENV !== 'production' &&
      !arrayBandWarnings.has(fieldKey)
    ) {
      arrayBandWarnings.add(fieldKey)
      console.warn(
        `List core groupable field "${fieldKey}" returned an array; only the first value is used for band grouping.`,
      )
    }
    if (value.length === 0) return EMPTY_OPTION
    return String(value[0])
  }
  return String(value)
}

function orderOptionIds<T>(
  optionIds: readonly string[],
  records: readonly T[],
  bandLevel: ResolvedBandLevel<T>,
  labelsByField: FacetIndex['labelsByField'],
  emptyOptionLabel: string | undefined,
  sortOrder: SortOrder,
): string[] {
  const order = bandLevel.field.bandOrder?.(records) ?? []
  const rankById = new Map(order.map((optionId, index) => [optionId, index]))
  const labelOf = (optionId: string) =>
    resolveOptionLabel(
      labelsByField[bandLevel.fieldKey],
      optionId,
      emptyOptionLabel,
    )

  return [...optionIds].sort((left, right) => {
    if (left === EMPTY_OPTION) return right === EMPTY_OPTION ? 0 : 1
    if (right === EMPTY_OPTION) return -1

    const leftRank = rankById.get(left) ?? Number.POSITIVE_INFINITY
    const rightRank = rankById.get(right) ?? Number.POSITIVE_INFINITY
    const direction = sortOrder === SortOrder.DESC ? -1 : 1
    if (leftRank !== rightRank) return (leftRank - rightRank) * direction

    return compareGroupsByLabel(left, right, labelOf) * direction
  })
}

function collectSelectableIds<T>(
  children: ReadonlyArray<BandTreeNode<T> | BandTreeRecord<T>>,
): readonly string[] {
  return children.flatMap(child =>
    child.kind === 'record' ? [child.recordId] : child.selectableIds,
  )
}
